import React from 'react';
import { Modal, Badge, List, Box, Text } from '@mantine/core';
import { Loan } from '../types/loan';
import { User, Building2, UserCircle, Users, DollarSign, MapPin, Calendar, Clock, FileText, AlertCircle, Circle } from 'lucide-react';
interface LoanDetailModalProps {
  loan: Loan | null;
  opened: boolean;
  onClose: () => void;
  onUpdate: (updatedLoan: Loan) => void;
}
export function LoanDetailModal({
  loan,
  opened,
  onClose,
  onUpdate
}: LoanDetailModalProps) {
  if (!loan) return null;
  return <Modal opened={opened} onClose={onClose} size="xl" padding={0} title={<div className="flex items-center gap-3 px-2" data-id="element-107">
          <span className="text-xl font-bold text-slate-900" data-id="element-108">
            Loan #{loan.loanNumber}
          </span>
          {loan.isUrgent && <Badge color="red" variant="filled" size="lg" radius="sm" className="bg-rose-600 font-bold shadow-sm" data-id="element-109">
              URGENT
            </Badge>}
        </div>} classNames={{
    header: 'border-b border-slate-200 p-6 bg-white',
    body: 'bg-slate-50',
    title: 'w-full'
  }} data-id="element-106">
      <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto" data-id="element-110">
        {/* Section 1: Client & Lender Details */}
        <div data-id="element-111">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-112">
            <div className="h-1 w-8 bg-blue-600 rounded-full" data-id="element-113"></div>
            Client & Lender Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-114">
            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-115">
              <div className="flex items-start gap-3" data-id="element-116">
                <div className="p-2 bg-blue-50 rounded-lg" data-id="element-117">
                  <User className="w-5 h-5 text-blue-600" data-id="element-118" />
                </div>
                <div data-id="element-119">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-120">
                    Client Name
                  </p>
                  <p className="font-semibold text-slate-900" data-id="element-121">
                    {loan.clientName}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-122">
              <div className="flex items-start gap-3" data-id="element-123">
                <div className="p-2 bg-emerald-50 rounded-lg" data-id="element-124">
                  <Building2 className="w-5 h-5 text-emerald-600" data-id="element-125" />
                </div>
                <div data-id="element-126">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-127">
                    Lender
                  </p>
                  <p className="font-semibold text-slate-900" data-id="element-128">{loan.lender}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-129">
              <div className="flex items-start gap-3" data-id="element-130">
                <div className="p-2 bg-purple-50 rounded-lg" data-id="element-131">
                  <UserCircle className="w-5 h-5 text-purple-600" data-id="element-132" />
                </div>
                <div data-id="element-133">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-134">
                    LO on File
                  </p>
                  <p className="font-semibold text-slate-900" data-id="element-135">
                    {loan.loanOfficer}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-136">
              <div className="flex items-start gap-3" data-id="element-137">
                <div className="p-2 bg-amber-50 rounded-lg" data-id="element-138">
                  <Users className="w-5 h-5 text-amber-600" data-id="element-139" />
                </div>
                <div data-id="element-140">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-141">
                    Contacts
                  </p>
                  <p className="font-semibold text-slate-900" data-id="element-142">
                    {loan.contacts || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-143">
              <div className="flex items-start gap-3" data-id="element-144">
                <div className="p-2 bg-green-50 rounded-lg" data-id="element-145">
                  <DollarSign className="w-5 h-5 text-green-600" data-id="element-146" />
                </div>
                <div data-id="element-147">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-148">
                    Loan Amount
                  </p>
                  <p className="font-bold text-slate-900 text-lg" data-id="element-149">
                    {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                  }).format(loan.amount)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-150">
              <div className="flex items-start gap-3" data-id="element-151">
                <div className="p-2 bg-indigo-50 rounded-lg" data-id="element-152">
                  <MapPin className="w-5 h-5 text-indigo-600" data-id="element-153" />
                </div>
                <div data-id="element-154">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-155">
                    Program / State
                  </p>
                  <p className="font-semibold text-slate-900" data-id="element-156">
                    {loan.program} - {loan.state}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Status & Dates */}
        <div data-id="element-157">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-158">
            <div className="h-1 w-8 bg-emerald-600 rounded-full" data-id="element-159"></div>
            Status & Dates
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-160">
            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-161">
              <div className="flex items-start gap-3" data-id="element-162">
                <div className="p-2 bg-blue-50 rounded-lg" data-id="element-163">
                  <Calendar className="w-5 h-5 text-blue-600" data-id="element-164" />
                </div>
                <div data-id="element-165">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-166">
                    Submitted On
                  </p>
                  <p className="font-semibold text-slate-900" data-id="element-167">
                    {loan.submittedDate}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-168">
              <div className="flex items-start gap-3" data-id="element-169">
                <div className="p-2 bg-amber-50 rounded-lg" data-id="element-170">
                  <Clock className="w-5 h-5 text-amber-600" data-id="element-171" />
                </div>
                <div data-id="element-172">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-173">
                    Rate Lock
                  </p>
                  <p className="font-semibold text-slate-900 italic" data-id="element-174">
                    {loan.rateLock || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-175">
              <div className="flex items-start gap-3" data-id="element-176">
                <div className="p-2 bg-purple-50 rounded-lg" data-id="element-177">
                  <FileText className="w-5 h-5 text-purple-600" data-id="element-178" />
                </div>
                <div data-id="element-179">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-180">
                    Payoff Request
                  </p>
                  <p className="font-semibold text-slate-900" data-id="element-181">
                    {loan.payoffReqStatus || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-182">
              <div className="flex items-start gap-3" data-id="element-183">
                <div className="p-2 bg-rose-50 rounded-lg" data-id="element-184">
                  <AlertCircle className="w-5 h-5 text-rose-600" data-id="element-185" />
                </div>
                <div data-id="element-186">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-187">
                    Payoff Due
                  </p>
                  <p className="font-semibold text-slate-900" data-id="element-188">
                    {loan.payoffDue || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-189">
              <div className="flex items-start gap-3" data-id="element-190">
                <div className="p-2 bg-emerald-50 rounded-lg" data-id="element-191">
                  <Calendar className="w-5 h-5 text-emerald-600" data-id="element-192" />
                </div>
                <div data-id="element-193">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-194">
                    Payoff Expires
                  </p>
                  <p className="font-semibold text-slate-900" data-id="element-195">
                    {loan.payoffExpDate || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-196">
              <div className="flex items-start gap-3" data-id="element-197">
                <div className="p-2 bg-indigo-50 rounded-lg" data-id="element-198">
                  <FileText className="w-5 h-5 text-indigo-600" data-id="element-199" />
                </div>
                <div data-id="element-200">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1" data-id="element-201">
                    HOI Request
                  </p>
                  <p className="font-semibold text-slate-900" data-id="element-202">
                    {loan.hoiReqStatus || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-203">
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm" data-id="element-204">
            <p className="text-xs text-slate-500 uppercase font-semibold mb-2" data-id="element-205">
              Title
            </p>
            <p className="font-semibold text-slate-900" data-id="element-206">
              {loan.titleInfo || 'N/A'}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm" data-id="element-207">
            <p className="text-xs text-slate-500 uppercase font-semibold mb-2" data-id="element-208">
              VOE
            </p>
            <p className="font-semibold text-slate-900 italic" data-id="element-209">
              {loan.voeStatus || 'N/A'}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm" data-id="element-210">
            <p className="text-xs text-slate-500 uppercase font-semibold mb-2" data-id="element-211">
              VA/FHA Case #
            </p>
            <p className="font-semibold text-slate-900 italic" data-id="element-212">
              {loan.vaFhaCase || 'N/A'}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm" data-id="element-213">
            <p className="text-xs text-slate-500 uppercase font-semibold mb-2" data-id="element-214">
              Water/Waiver
            </p>
            <p className="font-semibold text-slate-900 italic" data-id="element-215">
              {loan.waterWaiver || 'N/A'}
            </p>
          </div>
        </div>

        {/* Conditions - Read Only Bulleted Lists */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-216">
          {/* Outstanding Conditions */}
          <Box className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm" data-id="element-217">
            <Text size="xs" fw={700} tt="uppercase" c="slate.5" mb="md" style={{
            letterSpacing: '0.05em'
          }} data-id="element-218">
              Outstanding Conditions
            </Text>
            {loan.outstandingConditions.length > 0 ? <List spacing="xs" size="sm" icon={<Circle size={6} fill="currentColor" className="text-slate-400" data-id="element-220" />} data-id="element-219">
                {loan.outstandingConditions.map(condition => <List.Item key={condition.id} data-id="element-221">
                    <Text size="sm" c="slate.7" data-id="element-222">
                      {condition.text}
                    </Text>
                  </List.Item>)}
              </List> : <Text size="sm" c="dimmed" fs="italic" data-id="element-223">
                No outstanding conditions
              </Text>}
          </Box>

          {/* Loan Officer Conditions */}
          <Box className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm" data-id="element-224">
            <Text size="xs" fw={700} tt="uppercase" c="slate.5" mb="md" style={{
            letterSpacing: '0.05em'
          }} data-id="element-225">
              Loan Officer Conditions
            </Text>
            {loan.loanOfficerConditions.length > 0 ? <List spacing="xs" size="sm" icon={<Circle size={6} fill="currentColor" className="text-slate-400" data-id="element-227" />} data-id="element-226">
                {loan.loanOfficerConditions.map(condition => <List.Item key={condition.id} data-id="element-228">
                    <Text size="sm" c="slate.7" data-id="element-229">
                      {condition.text}
                    </Text>
                  </List.Item>)}
              </List> : <Text size="sm" c="dimmed" fs="italic" data-id="element-230">
                No loan officer conditions
              </Text>}
          </Box>
        </div>
      </div>
    </Modal>;
}